import {
  Avatar,
  Box, Button, Card, Divider, Stack, Typography,
} from '@mui/joy';
import React, { useEffect, useState } from 'react';
import { BsJournalCode } from 'react-icons/bs';
import { Parallax } from 'react-scroll-parallax';
import { FiArrowRight } from 'react-icons/fi';
import { Project } from 'assets/Projects';
import { Link } from 'react-router-dom';
import getProjects from 'components/projects/staticProjectsData';
import { Default, Mobile, useMobileMode } from '../Responsive';

/**
 * Beautifies a string
 * @param str The string to beautify
 * @returns The beautified string
 */
export function beautify(str: string) {
  return str.replace(/-/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
}

function ProjectCard({
  project,
}: {
  project: Project;
}) {
  const mobile = useMobileMode();
  const projectUrl = `/projects/${project.title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <Link
      to={projectUrl}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Card
        variant="outlined"
        sx={{
          padding: 0,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: mobile ? 'none' : 'translateY(-8px)',
            boxShadow: mobile ? 'none' : 'lg',
            '& .thumbnail': {
              transform: 'scale(1.05)',
            },
          },
        }}
      >
        {/* Large thumbnail image */}
        {project.thumbnail && (
          <Box
            component="img"
            src={project.thumbnail}
            alt={project.title}
            className="thumbnail"
            sx={{
              width: '100%',
              height: mobile ? '200px' : '300px',
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
            }}
          />
        )}
        {/* Title overlay */}
        <Box
          sx={{
            padding: 2,
            background: (theme) => theme.palette.background.surface,
            minHeight: mobile ? '80px' : '100px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography level="h4" fontWeight="lg">
            {project.title}
          </Typography>
        </Box>
      </Card>
    </Link>
  );
}

export default function Featured() {
  const mobile = useMobileMode();
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);

  useEffect(() => {
    const loadProjects = async () => {
      const projects = await getProjects();
      const featured = projects
        .filter((project) => project.featuredOrder && project.featuredOrder > 0)
        .sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));
      setFeaturedProjects(featured);
    };
    loadProjects();
  }, []);

  return (
    <Stack
      gap={3}
      marginBottom={mobile ? '5rem' : 0}
    >
      <Stack
        gap={2}
        alignItems={mobile ? 'center' : 'flex-start'}
      >
        <Mobile>
          <Avatar
            alt="Featured"
            color="info"
            sx={(theme) => ({
              position: 'relative',
              border: 'none',
              outline: `2px solid ${theme.palette.info[500]}`,
              boxShadow: `0 0 40px 5px rgba(${theme.palette.info.mainChannel} / 0.4)`,
              overflow: 'visible',
              marginTop: '3rem',
              marginBottom: '1rem',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-5rem',
                height: '5rem',
                width: '2px',
                background: `linear-gradient(to bottom, transparent, ${theme.palette.info[500]})`,
              },
            })}
          >
            <BsJournalCode />
          </Avatar>
        </Mobile>
        <Typography
          level="h2"
          sx={{ position: 'relative', textAlign: mobile ? 'center' : 'left' }}
          id="featured"
        >
          <Default>
            <Avatar
              alt="Featured"
              color="info"
              sx={(theme) => ({
                position: 'absolute',
                left: '-49px',
                top: '0',
                transform: 'translateX(-50%)',
                border: 'none',
                outline: `2px solid ${theme.palette.info[500]}`,
                boxShadow: `0 0 40px 5px rgba(${theme.palette.info.mainChannel} / 0.4)`,
              })}
            >
              <BsJournalCode />
            </Avatar>
          </Default>
          Featured
          {' '}
          <Typography
            color="info"
            alignItems="center"
            fontWeight="xl"
          >
            Projects
          </Typography>
        </Typography>
      </Stack>
      <Parallax
        opacity={[0, 1]}
        easing="easeOutBack"
        disabled={mobile}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 3,
          }}
        >
          {featuredProjects
            .slice(0, 6)
            .map((project) => (
              <ProjectCard
                key={project.title}
                project={project}
              />
            ))}
        </Box>
        <Divider />
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            flexWrap: mobile ? 'wrap' : 'nowrap',
            gap: mobile ? '2rem' : '1.5rem',
            alignItems: mobile ? 'center' : 'start',
            boxShadow: 'none',
            overflow: 'hidden',
            borderRadius: 0,
            background: 'transparent',
            padding: mobile ? '.5rem' : '0',
          }}
        >
          <Stack gap={mobile ? 1 : 0.5} alignItems={mobile ? 'center' : 'start'}>
            <Typography level="h2" fontWeight="200" textAlign={mobile ? 'center' : 'left'}>
              Projects &
              {' '}
              <Typography fontFamily="Righteous, sans-serif">
                Experiments
              </Typography>
            </Typography>
            <Typography level="body2" textColor="text.secondary" textAlign={mobile ? 'center' : 'left'}>
              Browse more projects in the dedicated explorer,
              a centralised hub for all my work and experiments.
            </Typography>
          </Stack>
          <Button
            component={Link}
            to="/projects"
            size="lg"
            variant="outlined"
            color="neutral"
            endDecorator={(
              <FiArrowRight />
            )}
            sx={{
              transition: 'all ease .2s',
              background: 'var(--joy-palette-text-primary)',
              color: 'var(--joy-palette-background-level1)',
              borderColor: 'var(--joy-palette-text-primary)',
              padding: '1 2',
              whiteSpace: 'nowrap',
              width: mobile ? '100%' : 'initial',

              '&:hover': {
                background: 'var(--joy-palette-background-body)',
                color: 'var(--joy-palette-text-primary)',
              },
              '&:active': {
                transform: 'scale(.98)',
              },
            }}
          >
            Explore more projects
          </Button>
        </Card>
      </Parallax>
    </Stack>
  );
}
